import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, rupee, istDateTime } from "@/lib/api";

export const MarginReadiness = () => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try { setD((await api.get("/execution/margin")).data); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const max = d ? Math.max(d.balance, ...d.rows.map((r) => r.need || 0)) || 1 : 1;
  return (
    <div className="panel col-span-12" data-testid="panel-margin-readiness">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 px-4 py-2.5">
        <span className="eyebrow">Margin readiness · today's legs vs your Dhan balance</span>
        <div className="flex items-center gap-3">
          {d && <span className="num text-[11px] text-slate-400">Available <b className="text-slate-100" data-testid="margin-balance">{rupee(d.balance)}</b> · <b data-testid="margin-ready-count" className={d.ready_count ? "up" : "down"}>{d.ready_count}/{d.rows.length}</b> ready</span>}
          <Button data-testid="button-refresh-margin" size="sm" variant="ghost" disabled={busy} onClick={load} className="h-7 gap-1 text-xs text-slate-400">
            <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> {busy ? "Asking Dhan…" : "Refresh"}
          </Button>
        </div>
      </div>
      {err && <p className="px-4 py-3 text-xs text-amber-400" data-testid="margin-error">{err}</p>}
      {!d && !err && <p className="px-4 py-4 text-sm text-slate-500">Pricing each strategy's legs with Dhan's margin calculator…</p>}
      {d && (
        <div className="overflow-x-auto">
          <table className="dense num w-full" data-testid="table-margin-readiness">
            <thead><tr><th className="!text-left">Strategy</th><th className="!text-left">Today's legs</th><th>Margin needed</th><th className="!text-left w-[28%]">vs balance</th><th>Status</th></tr></thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.kind} data-testid={`margin-row-${r.kind}`}>
                  <td className="!text-left text-slate-200">{r.label}{r.enabled && <span className="ml-1 text-[10px] text-emerald-400">ON</span>}</td>
                  <td className="!text-left text-[11px] text-slate-400">{r.error ? <span className="text-amber-400">{r.error}</span> : r.legs}</td>
                  <td data-testid={`margin-need-${r.kind}`}>{r.need != null ? rupee(r.need) : "—"}
                    {r.source && <span className={`ml-1 rounded px-1 text-[9px] font-semibold ${r.source === "dhan" ? "bg-indigo-500/15 text-indigo-300" : "bg-slate-700/60 text-slate-400"}`}>{r.source === "dhan" ? "DHAN" : "EST"}</span>}
                    {r.product === "MARGIN" && <span className="ml-1 text-[9px] text-amber-400/80">NRML</span>}</td>
                  <td className="!text-left">
                    {r.need != null && (
                      <div className="relative h-2 rounded bg-slate-800">
                        <div className={`absolute inset-y-0 left-0 rounded ${r.ready ? "bg-emerald-500/70" : "bg-red-500/60"}`} style={{ width: `${Math.min(100, (r.need / max) * 100)}%` }} />
                        <div className="absolute inset-y-[-3px] w-px bg-slate-200" style={{ left: `${Math.min(100, (d.balance / max) * 100)}%` }} title="Your balance" />
                      </div>
                    )}
                  </td>
                  <td data-testid={`margin-status-${r.kind}`} className={r.ready ? "up" : "down"}>{r.error ? "—" : r.ready ? "READY" : `SHORT ${rupee(r.shortfall)}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-4 py-2 text-[11px] text-slate-600">
            The white tick is your available balance. Margins come from Dhan's calculator for today's ATM legs (spreads: the larger of the call and put versions); EST means Dhan didn't answer and the bot's estimate is used. Checked {istDateTime(d.checked_at)} · expiry {d.expiry}.
          </p>
        </div>
      )}
    </div>
  );
};
